import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  savePendingExpense,
  uploadPendingExpenseAttachment,
  uploadGroupExpenseAttachment,
  usesGroupExpenseEndpoint,
  expenseKeys,
  rememberAttachmentName,
  type SaveBillInput,
} from '@entities/expense';
import { pendingExpenseKeys } from '@entities/pending-expense';
import { dashboardKeys } from '@entities/dashboard';
import { groupKeys } from '@entities/group';
import { userActivityKeys } from '@entities/user-activity';
import { AppError } from '@shared/api';
import { toast } from '@shared/lib/toast';
import { t } from '@shared/i18n';

/**
 * Save a "New Bill" (create or update) against the real `pending-expense` endpoint. Pessimistic
 * (money-of-record): await the server, then invalidate every cache the save can move — the user's
 * pending-expense list + dashboard summary, the group list (balances), the activity feed, and, when
 * the bill is tied to a group, that group's ledger. We never optimistically paint a balance the
 * server might reject.
 */
export function useSaveBill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveBillInput) => {
      try {
        return await savePendingExpense(input);
      } catch (error) {
        // A 403 on a bill save is a role/permission denial — e.g. the user was demoted to
        // view-only AFTER the modal's client-side guard read the cached role. Surface the same
        // friendly permission message the guards show; the backend's own wording here is its
        // internals ("Ask the Wallet Manager…") and reads wrong for a view-only member. The
        // global mutation-cache handler still does the toasting — exactly once.
        if (error instanceof AppError && error.status === 403) {
          throw new AppError({
            kind: error.kind,
            status: error.status,
            messageForUser: t.common.viewOnlyPermission,
            ...(error.code !== undefined ? { code: error.code } : {}),
            cause: error,
          });
        }
        throw error;
      }
    },
    onSuccess: (result, input) => {
      toast.success(t.newBill.toast.saved);
      // Background receipt-binary upload (best-effort; the expense is already saved). Mirrors the
      // mobile unawaited `uploadAttachmentInBackground` — the second leg after create/update, failure
      // swallowed (the user is never blocked, the expense just lacks a receipt until re-attached). The
      // upload endpoint follows the SAME split as the save: a group/recurring expense uploads to
      // `/api/v1/expense/{id}/attachment/upload`, a pending expense to `/api/v1/pending-expense/...`.
      if (input.receiptFile && result.id) {
        const originalName = input.receiptFile.name;
        const uploadPromise = usesGroupExpenseEndpoint(input)
          ? uploadGroupExpenseAttachment(input.groupId ?? '', result.id, input.receiptFile)
          : uploadPendingExpenseAttachment(result.id, input.receiptFile);
        void uploadPromise
          .then((url) => rememberAttachmentName(url, originalName))
          .catch(() => {
            /* best-effort: swallow — the expense is saved, only the receipt binary is missing */
          });
      }
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: pendingExpenseKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: dashboardKeys.all() }),
        queryClient.invalidateQueries({ queryKey: groupKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: userActivityKeys.all() }),
        input.groupId
          ? queryClient.invalidateQueries({
              queryKey: expenseKeys.list({ groupId: input.groupId }),
            })
          : Promise.resolve(),
      ]);
    },
    // No onError here — the global mutation-cache handler (shared/api/queryClient.ts) already
    // toasts every mutation failure; a local toast would show the same message twice.
  });
}
